/**
 * PasswordChangeModal Component
 *
 * Shared modal for password change functionality.
 * Used by both regular users and admin users.
 * Extracted from UserAccountManagement for reusability.
 */

// 1. External dependencies
import React, { useState, useCallback } from 'react';
import { Eye, EyeOff } from 'lucide-react';

// 2. Internal absolute imports
import {
  Button,
  Input,
  Form,
  Alert,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter
} from '@enfomentis/ui';
import { useAuth } from '@/hooks/useAuth';
import type { ChangePasswordRequest, AuthErrorCode } from '@/types/auth';

/**
 * Props for PasswordChangeModal
 */
export interface PasswordChangeModalProps {
  isOpen: boolean;
  onOpenChange: () => void;
  onSuccess?: () => void;
}

/**
 * Password Change Modal Component
 *
 * Features:
 * - Current password verification
 * - New password with strength requirements
 * - Confirm password matching
 * - Real-time validation
 * - Accessibility (show/hide password, keyboard navigation)
 * - Success state with auto-close
 */
export const PasswordChangeModal: React.FC<PasswordChangeModalProps> = ({
  isOpen,
  onOpenChange,
  onSuccess
}) => {
  const { user, changePassword } = useAuth();

  // Form state
  const [formData, setFormData] = useState<ChangePasswordRequest>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isCurrentPasswordVisible, setIsCurrentPasswordVisible] = useState(false);
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Toggle password visibility functions
  const toggleCurrentPasswordVisibility = useCallback(() => {
    setIsCurrentPasswordVisible(prev => !prev);
  }, []);

  const toggleNewPasswordVisibility = useCallback(() => {
    setIsNewPasswordVisible(prev => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setIsConfirmPasswordVisible(prev => !prev);
  }, []);

  // Password validation functions
  const validateCurrentPassword = (value: string): string | null => {
    if (!value) return "Current password is required";
    return null;
  };

  const validateNewPassword = (value: string): string | null => {
    if (!value) return "New password is required";
    if (value.length < 8) return "Password must be at least 8 characters long";
    if (!/(?=.*[a-z])/.test(value)) return "Password must contain at least one lowercase letter";
    if (!/(?=.*[A-Z])/.test(value)) return "Password must contain at least one uppercase letter";
    if (!/(?=.*\d)/.test(value)) return "Password must contain at least one number";
    if (value === formData.currentPassword) return "New password must be different from current password";
    return null;
  };

  const validateConfirmPassword = (value: string): string | null => {
    if (!value) return "Please confirm your new password";
    if (value !== formData.newPassword) return "Passwords do not match";
    return null;
  };

  // Validation state - show errors after submit attempt
  const currentPasswordError = hasSubmitted ? validateCurrentPassword(formData.currentPassword) : null;
  const newPasswordError = hasSubmitted ? validateNewPassword(formData.newPassword) : null;
  const confirmPasswordError = hasSubmitted ? validateConfirmPassword(formData.confirmPassword) : null;

  // Reset form state
  const resetForm = useCallback(() => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setServerErrors({});
    setHasSubmitted(false);
    setIsSubmitting(false);
    setIsSuccess(false);
    setIsCurrentPasswordVisible(false);
    setIsNewPasswordVisible(false);
    setIsConfirmPasswordVisible(false);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    resetForm();
    onOpenChange();
  }, [resetForm, onOpenChange]);

  // Handle password change form submission
  const handlePasswordChange = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setHasSubmitted(true);

    // Client-side validation before submission
    const currentPasswordValidationError = validateCurrentPassword(formData.currentPassword);
    const newPasswordValidationError = validateNewPassword(formData.newPassword);
    const confirmPasswordValidationError = validateConfirmPassword(formData.confirmPassword);

    if (currentPasswordValidationError || newPasswordValidationError || confirmPasswordValidationError) {
      return; // Don't submit if there are validation errors
    }

    setIsSubmitting(true);
    setServerErrors({});

    try {
      const result = await changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      });

      if (result.success) {
        setIsSuccess(true);
        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess();
        }
        // Reset form after short delay to show success message
        setTimeout(() => {
          handleModalClose();
        }, 2000);
      } else {
        // Map authentication errors to form field errors
        const errors: Record<string, string> = {};

        switch (result.error as AuthErrorCode) {
          case 'INVALID_CREDENTIALS':
            errors['currentPassword'] = 'Current password is incorrect';
            break;
          case 'VALIDATION_ERROR':
            errors['general'] = result.message || 'Please check your password requirements';
            break;
          case 'RATE_LIMITED':
            errors['general'] = 'Too many password change attempts. Please try again later';
            break;
          default:
            errors['general'] = result.message || 'Password change failed. Please try again';
        }

        setServerErrors(errors);
      }
    } catch {
      setServerErrors({ 'general': 'Network error occurred. Please check your connection and try again' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="lg"
      placement="center"
      backdrop="blur"
      scrollBehavior="inside"
      isDismissable={!isSubmitting}
      isKeyboardDismissDisabled={isSubmitting}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h3 className="text-xl font-semibold">Change Password</h3>
              <p className="text-sm text-text-secondary font-normal">
                Update your account password with a new secure password
              </p>
            </ModalHeader>

            <ModalBody>
              {isSuccess ? (
                // Success state
                <div className="text-center py-8">
                  <div className="text-success-600 text-6xl mb-4">✓</div>
                  <h4 className="text-lg font-medium text-text-primary mb-2">
                    Password Changed Successfully
                  </h4>
                  <p className="text-text-secondary">
                    Your password has been updated. This modal will close automatically.
                  </p>
                </div>
              ) : (
                // Form state
                <Form
                  validationBehavior="aria"
                  validationErrors={serverErrors}
                  onSubmit={handlePasswordChange}
                  className="space-y-6"
                >
                  {/* Hidden username field for password manager accessibility */}
                  <input
                    type="email"
                    name="username"
                    autoComplete="username"
                    value={user?.email || ''}
                    readOnly
                    tabIndex={-1}
                    aria-hidden="true"
                    style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}
                  />

                  {/* Current Password Input */}
                  <Input
                    type={isCurrentPasswordVisible ? "text" : "password"}
                    label="Current Password"
                    placeholder="Enter your current password"
                    value={formData.currentPassword}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, currentPassword: value }))}
                    validate={validateCurrentPassword}
                    isRequired
                    autoComplete="current-password"
                    errorMessage={serverErrors['currentPassword'] || currentPasswordError}
                    isInvalid={!!serverErrors['currentPassword'] || !!currentPasswordError}
                    endContent={
                      <button
                        className="focus:outline-none"
                        type="button"
                        onClick={toggleCurrentPasswordVisibility}
                        aria-label={isCurrentPasswordVisible ? "Hide password" : "Show password"}
                      >
                        {isCurrentPasswordVisible ? (
                          <Eye className="w-4 h-4 text-default-400 pointer-events-none" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-default-400 pointer-events-none" />
                        )}
                      </button>
                    }
                    classNames={{
                      input: "text-base", // 16px font size to prevent mobile zoom
                    }}
                  />

                  {/* New Password Input */}
                  <Input
                    type={isNewPasswordVisible ? "text" : "password"}
                    label="New Password"
                    placeholder="Enter your new password"
                    value={formData.newPassword}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, newPassword: value }))}
                    validate={validateNewPassword}
                    isRequired
                    autoComplete="new-password"
                    errorMessage={serverErrors['newPassword'] || newPasswordError}
                    isInvalid={!!serverErrors['newPassword'] || !!newPasswordError}
                    endContent={
                      <button
                        className="focus:outline-none"
                        type="button"
                        onClick={toggleNewPasswordVisibility}
                        aria-label={isNewPasswordVisible ? "Hide password" : "Show password"}
                      >
                        {isNewPasswordVisible ? (
                          <Eye className="w-4 h-4 text-default-400 pointer-events-none" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-default-400 pointer-events-none" />
                        )}
                      </button>
                    }
                    classNames={{
                      input: "text-base", // 16px font size to prevent mobile zoom
                    }}
                  />

                  {/* Confirm Password Input */}
                  <Input
                    type={isConfirmPasswordVisible ? "text" : "password"}
                    label="Confirm New Password"
                    placeholder="Confirm your new password"
                    value={formData.confirmPassword}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, confirmPassword: value }))}
                    validate={validateConfirmPassword}
                    isRequired
                    autoComplete="new-password"
                    errorMessage={serverErrors['confirmPassword'] || confirmPasswordError}
                    isInvalid={!!serverErrors['confirmPassword'] || !!confirmPasswordError}
                    endContent={
                      <button
                        className="focus:outline-none"
                        type="button"
                        onClick={toggleConfirmPasswordVisibility}
                        aria-label={isConfirmPasswordVisible ? "Hide password" : "Show password"}
                      >
                        {isConfirmPasswordVisible ? (
                          <Eye className="w-4 h-4 text-default-400 pointer-events-none" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-default-400 pointer-events-none" />
                        )}
                      </button>
                    }
                    classNames={{
                      input: "text-base", // 16px font size to prevent mobile zoom
                    }}
                  />

                  {/* General Error Display */}
                  {serverErrors['general'] && (
                    <Alert
                      color="warning"
                      variant="flat"
                      description={serverErrors['general']}
                    />
                  )}

                  {/* Password Requirements */}
                  <div className="p-4 bg-default-50 rounded-lg">
                    <h5 className="text-sm font-medium text-text-primary mb-2">
                      Password Requirements:
                    </h5>
                    <ul className="text-sm text-text-secondary space-y-1">
                      <li>• At least 8 characters long</li>
                      <li>• At least one lowercase letter</li>
                      <li>• At least one uppercase letter</li>
                      <li>• At least one number</li>
                      <li>• Different from your current password</li>
                    </ul>
                  </div>
                </Form>
              )}
            </ModalBody>

            {!isSuccess && (
              <ModalFooter>
                <Button
                  color="default"
                  variant="light"
                  onPress={handleModalClose}
                  isDisabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  type="submit"
                  isLoading={isSubmitting}
                  onPress={() => {
                    const form = document.querySelector('form');
                    if (form) {
                      const event = new Event('submit', { bubbles: true, cancelable: true });
                      form.dispatchEvent(event);
                    }
                  }}
                >
                  {isSubmitting ? 'Changing Password...' : 'Change Password'}
                </Button>
              </ModalFooter>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
